
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp, ExternalLink, X } from "lucide-react";
import { BomItem, BomItemLink } from "@/types/tutorial";
import CollapsibleSection from "./CollapsibleSection";
import RichTextEditor from "./RichTextEditor";

interface BomManagerProps {
  bom: BomItem[];
  onChange: (bom: BomItem[]) => void;
}

const BomManager: React.FC<BomManagerProps> = ({ bom, onChange }) => {
  const [allExpanded, setAllExpanded] = useState(true);

  const addItem = () => {
    const newItem: BomItem = {
      id: `bom-${Date.now()}`,
      name: "",
      quantity: 1,
      categories: [],
      links: [],
      description: ""
    };
    onChange([...bom, newItem]);
    
    // Auto-focus the new item after a brief delay
    setTimeout(() => {
      const newItemElement = document.querySelector(`[data-bom-id="${newItem.id}"] input`);
      if (newItemElement) {
        (newItemElement as HTMLElement).focus();
        newItemElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const updateItem = (id: string, updates: Partial<BomItem>) => {
    onChange(bom.map(item => 
      item.id === id ? { ...item, ...updates } : item
    ));
  };

  const removeItem = (id: string) => {
    onChange(bom.filter(item => item.id !== id));
  };

  const toggleAllItems = () => {
    setAllExpanded(!allExpanded);
  };

  return (
    <CollapsibleSection 
      title="Bill of Materials (BOM)"
      defaultExpanded={true}
      headerActions={
        bom.length > 0 && (
          <Button variant="outline" size="sm" onClick={toggleAllItems}>
            {allExpanded ? <ChevronUp size={16} className="mr-2" /> : <ChevronDown size={16} className="mr-2" />}
            {allExpanded ? "Collapse All" : "Expand All"}
          </Button>
        )
      }
    >
      <div className="space-y-4">
        {bom.map((item, index) => (
          <CollapsibleBomItem
            key={item.id}
            item={item}
            index={index}
            onUpdate={(updates) => updateItem(item.id, updates)}
            onRemove={() => removeItem(item.id)}
            allExpanded={allExpanded}
          />
        ))}
        
        <div className="sticky bottom-4 z-10 flex justify-center">
          <Button variant="outline" size="sm" onClick={addItem} className="bg-background shadow-lg border-2">
            <Plus size={14} className="mr-1" />
            Add Item
          </Button>
        </div>
        
        {bom.length === 0 && (
          <div className="text-center py-4 text-muted-foreground text-sm">
            No items yet. Click "Add Item" to get started.
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

const CollapsibleBomItem: React.FC<{
  item: BomItem;
  index: number;
  onUpdate: (updates: Partial<BomItem>) => void;
  onRemove: () => void;
  allExpanded: boolean;
}> = ({ item, index, onUpdate, onRemove, allExpanded }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [newCategory, setNewCategory] = useState("");

  React.useEffect(() => {
    setIsExpanded(allExpanded);
  }, [allExpanded]);

  const addCategory = () => {
    if (newCategory.trim() && !(item.categories || []).includes(newCategory.trim())) {
      const updatedCategories = [...(item.categories || []), newCategory.trim()];
      onUpdate({ categories: updatedCategories });
      setNewCategory("");
    }
  };

  const removeCategory = (categoryToRemove: string) => {
    const updatedCategories = (item.categories || []).filter(cat => cat !== categoryToRemove);
    onUpdate({ categories: updatedCategories });
  };

  const addLink = () => {
    const newLink: BomItemLink = {
      id: `link-${Date.now()}`,
      name: "",
      url: ""
    };
    const updatedLinks = [...(item.links || []), newLink];
    onUpdate({ links: updatedLinks });
  };

  const updateLink = (linkId: string, updates: Partial<BomItemLink>) => {
    const updatedLinks = (item.links || []).map(link =>
      link.id === linkId ? { ...link, ...updates } : link
    );
    onUpdate({ links: updatedLinks });
  };

  const removeLink = (linkId: string) => {
    const updatedLinks = (item.links || []).filter(link => link.id !== linkId);
    onUpdate({ links: updatedLinks });
  };

  return (
    <div 
      className="bg-gradient-to-r from-muted/20 to-muted/5 rounded-lg p-4 space-y-3 border border-border/30"
      data-bom-id={item.id}
    >
      <div className="flex items-start gap-2">
        <GripVertical size={16} className="text-muted-foreground cursor-grab mt-2" />
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-0 h-auto"
              >
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
              <Label className="text-sm font-medium bg-background/80 px-2 py-1 rounded">
                Item {index + 1}
              </Label>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="text-destructive hover:text-destructive h-6 w-6 p-0"
            >
              <Trash2 size={12} />
            </Button>
          </div>

          {!isExpanded && (
            <div className="text-sm text-muted-foreground pl-6 bg-background/40 p-2 rounded">
              {item.name || "Unnamed item"} - Qty: {item.quantity}
              {item.categories && item.categories.length > 0 && (
                <span className="ml-2">• {item.categories.join(", ")}</span>
              )}
              {item.links && item.links.length > 0 && (
                <span className="ml-2">• {item.links.length} link{item.links.length > 1 ? 's' : ''}</span>
              )}
            </div>
          )}

          {isExpanded && (
            <div className="space-y-3 bg-background/40 p-3 rounded">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={item.name}
                    onChange={(e) => onUpdate({ name: e.target.value })}
                    placeholder="Item name"
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Quantity</Label>
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => onUpdate({ quantity: parseInt(e.target.value) || 1 })}
                    min="1"
                    className="text-sm"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Categories</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(item.categories || []).map((category, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {category}
                      <button
                        onClick={() => removeCategory(category)}
                        className="ml-1 text-xs hover:text-destructive"
                      >
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Add category (e.g., Hardware, Electronics)"
                    className="text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCategory();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCategory}
                    className="h-8 px-3 text-xs"
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Purchase Links</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLink}
                    className="h-6 px-2 text-xs"
                  >
                    <Plus size={12} className="mr-1" />
                    Add Link
                  </Button>
                </div>
                
                {item.links && item.links.length > 0 && (
                  <div className="space-y-2">
                    {item.links.map((link, linkIndex) => (
                      <div key={link.id} className="flex gap-2 items-center bg-background/60 p-2 rounded">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <Input
                            value={link.name}
                            onChange={(e) => updateLink(link.id, { name: e.target.value })}
                            placeholder="Link name (e.g., 'Buy on AliExpress')"
                            className="text-xs"
                          />
                          <Input
                            value={link.url}
                            onChange={(e) => updateLink(link.id, { url: e.target.value })}
                            placeholder="https://example.com"
                            className="text-xs"
                          />
                        </div>
                        {link.url && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(link.url, '_blank')}
                            className="h-6 w-6 p-0"
                          >
                            <ExternalLink size={12} />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLink(link.id)}
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <RichTextEditor
                  content={item.description || ""}
                  onChange={(content) => onUpdate({ description: content })}
                  placeholder="Item description, notes, or specifications..."
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BomManager;
